import { createContext, useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ProjectDto, TagDto } from '@pb/shared';

export interface ProjectContextValue {
  project: ProjectDto;
  tags: TagDto[];
  tagsVersion: number;
  reloadTags: () => Promise<void>;
  reloadProject: () => Promise<void>;
  setDetailCrumb: (label: string | null) => void;
}

export const ProjectContext = createContext<ProjectContextValue | null>(null);

export function useProject(): ProjectContextValue {
  const value = useContext(ProjectContext);
  if (!value) throw new Error('useProject outside a project');
  return value;
}

export function useDetailCrumb(label: string | null | undefined): void {
  const { setDetailCrumb } = useProject();
  useEffect(() => {
    setDetailCrumb(label ?? null);
    return () => setDetailCrumb(null);
  }, [label, setDetailCrumb]);
}

export function useProjectId(): number {
  return useProject().project.id;
}

export function useProjectOwnerGuard(ownerId: number | undefined): boolean {
  const { project } = useProject();
  const navigate = useNavigate();
  const location = useLocation();
  const mismatched = ownerId !== undefined && ownerId !== project.id;

  useEffect(() => {
    if (!mismatched || ownerId === undefined) return;
    const segments = location.pathname.split('/');
    segments[2] = String(ownerId);
    navigate(`${segments.join('/')}${location.search}`, { replace: true });
  }, [mismatched, ownerId, location.pathname, location.search, navigate]);

  return mismatched;
}
